/**
 * Application Configuration
 *
 * Central configuration file for the application.
 * Feature flags can be toggled to enable/disable functionality.
 */

import { features, type Features } from './features';

export interface AppConfig {
  /** Application name displayed in UI */
  appName: string;

  /** Base URL for sharing links (production URL) */
  appUrl: string;

  /** Base URL for the student join subdomain */
  joinUrl: string;

  /** Feature flags */
  features: Features;

  /** Supabase configuration */
  supabase: {
    url: string;
    anonKey: string;
  };

  /** Authentication configuration */
  auth: {
    /** Available OAuth providers */
    providers: Array<'google' | 'github' | 'discord' | 'apple'>;
    /** Enable email/password authentication */
    emailPassword: boolean;
    /** Enable new user registration */
    registrationEnabled: boolean;
    /** Redirect path after login */
    loginRedirect: string;
    /** Redirect path after logout */
    logoutRedirect: string;
  };

  /** Stripe configuration */
  stripe: {
    publishableKey: string | undefined;
  };
}

export const config: AppConfig = {
  appName: import.meta.env.VITE_APP_NAME || 'SciModeling',

  // Use env var for production URL, fallback to empty string (will use window.location.origin)
  appUrl: import.meta.env.VITE_APP_URL || '',

  // Use env var for the student join subdomain, fallback to empty string (dev override applies)
  joinUrl: import.meta.env.VITE_JOIN_URL || '',

  features,

  supabase: {
    url: import.meta.env.VITE_SUPABASE_URL || '',
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  },

  auth: {
    providers: ['google'],
    emailPassword: true,
    registrationEnabled: true,
    loginRedirect: '/dashboard',
    logoutRedirect: '/',
  },

  stripe: {
    publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined,
  },
};

export default config;
